/**
 * Draw Detection for Poker Reborn
 *
 * Pure utility — detects flush draws, straight draws, and backdoor draws
 * from hero's hole cards + community board.
 *
 * @see docs/screens/room/DRAW_PREDICTION.md - Visual specs, detection algorithm
 * @see tasks/active/T71_DRAW_PREDICTION_OVERLAY.md - Original task spec
 * @see src/utils/handStrength.ts - Made-hand detection (used to skip draws)
 */

import type { Card, Rank, Suit } from '@/types/game';
import { getPostflopStrength } from '@/utils/handStrength';

/** Information about a detected draw */
export interface DrawInfo {
  /** Display name: "Flush Draw", "Open-Ended", "Gutshot", "Backdoor Flush", "Backdoor Straight" */
  drawName: string;
  /** Number of outs that complete this draw (for backdoor: outs on next street) */
  outs: number;
  /** Specific cards that would complete the draw (next needed card for backdoor) */
  neededCards: Card[];
  /** For flush draws — the suit being chased */
  suitColor?: Suit;
}

const RANK_VALUES: Record<Rank, number> = {
  '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8,
  '9': 9, 'T': 10, 'J': 11, 'Q': 12, 'K': 13, 'A': 14,
};

const ALL_RANKS: Rank[] = ['2', '3', '4', '5', '6', '7', '8', '9', 'T', 'J', 'Q', 'K', 'A'];
const ALL_SUITS: Suit[] = ['c', 'd', 'h', 's'];

/** Build a full 52-card deck minus the known cards */
function buildRemainingDeck(knownCards: Card[]): Card[] {
  const known = new Set(knownCards.map((c) => `${c.rank}${c.suit}`));
  const remaining: Card[] = [];
  for (const rank of ALL_RANKS) {
    for (const suit of ALL_SUITS) {
      if (!known.has(`${rank}${suit}`)) {
        remaining.push({ rank, suit });
      }
    }
  }
  return remaining;
}

/** Detect flush draws — 4 cards of same suit, or 3 cards (backdoor) */
function detectFlushDraws(allCards: Card[], remainingDeck: Card[]): DrawInfo | null {
  const suitCounts = new Map<Suit, number>();
  for (const card of allCards) {
    suitCounts.set(card.suit, (suitCounts.get(card.suit) ?? 0) + 1);
  }

  // Check 4-card flush draw first (strongest)
  for (const [suit, count] of suitCounts) {
    if (count === 4) {
      const neededCards = remainingDeck.filter((c) => c.suit === suit);
      return {
        drawName: 'Flush Draw',
        outs: neededCards.length,
        neededCards,
        suitColor: suit,
      };
    }
  }

  // Check 3-card backdoor flush draw
  for (const [suit, count] of suitCounts) {
    if (count === 3) {
      const neededCards = remainingDeck.filter((c) => c.suit === suit);
      return {
        drawName: 'Backdoor Flush',
        outs: neededCards.length,
        neededCards,
        suitColor: suit,
      };
    }
  }

  return null;
}

/** Find cards in remaining deck matching a rank value (handles ace=1/14 duality) */
function findCardsForRankValue(remainingDeck: Card[], rankValue: number): Card[] {
  return remainingDeck.filter((c) => {
    const rv = RANK_VALUES[c.rank];
    return rv === rankValue || (rankValue === 1 && rv === 14);
  });
}

/** Detect straight draws — OESD, gutshot, and backdoor straight */
function detectStraightDraws(allCards: Card[], remainingDeck: Card[]): DrawInfo[] {
  const rankValues = new Set(allCards.map((c) => RANK_VALUES[c.rank]));
  // Ace also counts as 1 for wheel draws
  if (rankValues.has(14)) {
    rankValues.add(1);
  }

  const draws: DrawInfo[] = [];

  // --- Step 1: Find OESD — 4 consecutive ranks that can extend both ways ---
  let foundOesd = false;
  for (let start = 1; start <= 11; start++) {
    const seq = [start, start + 1, start + 2, start + 3];
    if (!seq.every((r) => rankValues.has(r))) continue;

    const lowExtend = start - 1;
    const highExtend = start + 4;
    const canExtendLow = lowExtend >= 1;
    const canExtendHigh = highExtend <= 14;

    if (!canExtendLow && !canExtendHigh) continue;

    const neededRankValues: number[] = [];
    if (canExtendLow) neededRankValues.push(lowExtend);
    if (canExtendHigh) neededRankValues.push(highExtend);

    const neededCards = neededRankValues.flatMap((rv) =>
      findCardsForRankValue(remainingDeck, rv),
    );

    const isOpenEnded = canExtendLow && canExtendHigh;

    draws.push({
      drawName: isOpenEnded ? 'Open-Ended' : 'Gutshot',
      outs: neededCards.length,
      neededCards,
    });

    if (isOpenEnded) {
      foundOesd = true;
      break;
    }
  }

  // --- Step 2: Find gutshots — 4 of 5 consecutive with interior gap ---
  if (!foundOesd) {
    const coveredRankValues = new Set(
      draws.flatMap((d) => d.neededCards.map((c) => RANK_VALUES[c.rank])),
    );

    for (let low = 1; low <= 10; low++) {
      const windowRanks = [low, low + 1, low + 2, low + 3, low + 4];
      const present = windowRanks.filter((r) => rankValues.has(r));
      const missing = windowRanks.filter((r) => !rankValues.has(r));

      if (present.length !== 4 || missing.length !== 1) continue;

      const missingRank = missing[0]!;
      if (missingRank === windowRanks[0] || missingRank === windowRanks[4]) continue;

      const neededCards = findCardsForRankValue(remainingDeck, missingRank);
      if (neededCards.every((c) => coveredRankValues.has(RANK_VALUES[c.rank]))) continue;

      draws.push({
        drawName: 'Gutshot',
        outs: neededCards.length,
        neededCards,
      });
      break;
    }
  }

  // --- Step 3: Backdoor straight — 3 of 5 consecutive with 2 gaps ---
  // Only if no standard straight draws found
  if (draws.length === 0) {
    for (let low = 1; low <= 10; low++) {
      const windowRanks = [low, low + 1, low + 2, low + 3, low + 4];
      const present = windowRanks.filter((r) => rankValues.has(r));

      if (present.length !== 3) continue;

      // Need at least one end open (not a dead-end like needing 2 interior cards)
      const missing = windowRanks.filter((r) => !rankValues.has(r));
      const neededCards = missing.flatMap((rv) => findCardsForRankValue(remainingDeck, rv));

      if (neededCards.length > 0) {
        draws.push({
          drawName: 'Backdoor Str.',
          outs: neededCards.length,
          neededCards,
        });
        break;
      }
    }
  }

  return draws;
}

/**
 * Detect all draws from hero's hole cards + board cards.
 *
 * @param holeCards - Hero's 2 hole cards
 * @param boardCards - Community cards (3 on flop, 4 on turn)
 * @returns Up to 2 draws sorted by outs descending, or empty array
 */
export function detectDraws(holeCards: Card[], boardCards: Card[]): DrawInfo[] {
  const allCards = [...holeCards, ...boardCards];

  // Need at least 5 cards (2 hole + 3 board) for draw detection
  if (allCards.length < 5) return [];

  const remainingDeck = buildRemainingDeck(allCards);
  const draws: DrawInfo[] = [];

  // Check if hero already has a made flush (strength >= 6) — skip flush draw
  const madeStrength = getPostflopStrength(allCards);
  if (madeStrength < 6) {
    const flushDraw = detectFlushDraws(allCards, remainingDeck);
    if (flushDraw) draws.push(flushDraw);
  }

  // Check if hero already has a made straight (strength >= 5) — skip straight draws
  if (madeStrength < 5) {
    const straightDraws = detectStraightDraws(allCards, remainingDeck);
    draws.push(...straightDraws);
  }

  // Sort by outs descending, cap at 2
  draws.sort((a, b) => b.outs - a.outs);
  return draws.slice(0, 2);
}
